import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { WebsiteTask, WebsiteName, TaskStatus, TaskPriority } from '@/types/website';

export function useWebsiteTasks(websiteFilter?: WebsiteName, pageTagFilter?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading, error } = useQuery({
    queryKey: ['website-tasks', websiteFilter, pageTagFilter],
    queryFn: async () => {
      let query = supabase
        .from('website_tasks')
        .select(`
          *,
          assigned_profile:profiles!website_tasks_assigned_to_fkey(display_name, email),
          created_profile:profiles!website_tasks_created_by_fkey(display_name, email)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (websiteFilter) {
        query = query.eq('website', websiteFilter);
      }

      if (pageTagFilter) {
        query = query.contains('page_tags', [pageTagFilter]);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as WebsiteTask[];
    },
    enabled: !!user,
  });

  const createTask = useMutation({
    mutationFn: async (task: {
      title: string;
      description?: string;
      website: WebsiteName;
      status?: TaskStatus;
      priority?: TaskPriority;
      assigned_to?: string;
      due_date?: string;
      labels?: string[];
      page_tags?: string[];
    }) => {
      const { data, error } = await supabase
        .from('website_tasks')
        .insert({
          ...task,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['website-page-tags'] });
      toast.success('Taak aangemaakt');
    },
    onError: (error) => {
      console.error('Error creating task:', error);
      toast.error('Fout bij aanmaken taak');
    },
  });

  const updateTask = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WebsiteTask> & { id: string }) => {
      const { data, error } = await supabase
        .from('website_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['website-page-tags'] });
      toast.success('Taak bijgewerkt');
    },
    onError: (error) => {
      console.error('Error updating task:', error);
      toast.error('Fout bij bijwerken taak');
    },
  });

  const bulkUpdateTasks = useMutation({
    mutationFn: async ({ ids, updates }: { 
      ids: string[]; 
      updates: Partial<Pick<WebsiteTask, 'website' | 'status' | 'priority' | 'page_tags'>> 
    }) => {
      // For page_tags, we need to append rather than replace
      if (updates.page_tags && updates.page_tags.length > 0) {
        // First get current tasks to merge page_tags
        const { data: currentTasks, error: fetchError } = await supabase
          .from('website_tasks')
          .select('id, page_tags')
          .in('id', ids);
        
        if (fetchError) throw fetchError;

        // Update each task with merged page_tags
        const updatePromises = currentTasks?.map(task => {
          const existingTags = task.page_tags || [];
          const newTags = updates.page_tags || [];
          const mergedTags = [...new Set([...existingTags, ...newTags])];
          
          const taskUpdates = { ...updates, page_tags: mergedTags };
          
          return supabase
            .from('website_tasks')
            .update(taskUpdates)
            .eq('id', task.id);
        }) || [];

        await Promise.all(updatePromises);
      } else {
        const { error } = await supabase
          .from('website_tasks')
          .update(updates)
          .in('id', ids);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['website-page-tags'] });
      toast.success('Taken bijgewerkt');
    },
    onError: (error) => {
      console.error('Error bulk updating tasks:', error);
      toast.error('Fout bij bijwerken taken');
    },
  });

  const bulkDeleteTasks = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from('website_tasks')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', ids);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
      toast.success('Taken verwijderd');
    },
    onError: (error) => {
      console.error('Error bulk deleting tasks:', error);
      toast.error('Fout bij verwijderen taken');
    },
  });

  const deleteTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('website_tasks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-tasks'] });
      toast.success('Taak verwijderd');
    },
    onError: (error) => {
      console.error('Error deleting task:', error);
      toast.error('Fout bij verwijderen taak');
    },
  });

  return {
    tasks,
    isLoading,
    error,
    createTask,
    updateTask,
    bulkUpdateTasks,
    bulkDeleteTasks,
    deleteTask,
  };
}
